import { useCallback, useEffect, useRef, useState } from 'react'
import {
	useIonViewWillEnter,
	useIonViewWillLeave,
} from '@ionic/react'

/**
 * Keeps the screen awake while active and while the Ionic view is visible.
 * Re-acquires the lock when the page regains visibility (e.g. tab switch or app resume).
 * Pass `false` to release the lock without unmounting.
 */
export function useWakeLock(active = true) {
	const [ isIonViewActive, setIsIonViewActive ] = useState(false)
	const wakeLockRef = useRef<WakeLockSentinel | null>(null)
	const shouldKeepAwakeRef = useRef(false)

	const release = useCallback(() => {
		const wakeLock = wakeLockRef.current
		wakeLockRef.current = null

		if (wakeLock && !wakeLock.released) {
			void wakeLock.release()
		}
	}, [])

	const request = useCallback(async () => {
		if (
			!shouldKeepAwakeRef.current
			|| document.visibilityState !== 'visible'
			|| !('wakeLock' in navigator)
			|| (wakeLockRef.current && !wakeLockRef.current.released)
		) {
			return
		}

		try {
			const wakeLock = await navigator.wakeLock.request('screen')

			if (!shouldKeepAwakeRef.current || document.visibilityState !== 'visible') {
				void wakeLock.release()
				return
			}

			wakeLockRef.current = wakeLock
			wakeLock.addEventListener('release', () => {
				if (wakeLockRef.current === wakeLock) {
					wakeLockRef.current = null
				}
			})
		} catch { /* user-agent denied wake lock */ }
	}, [])

	useIonViewWillEnter(() => {
		setIsIonViewActive(true)
	})

	useIonViewWillLeave(() => {
		setIsIonViewActive(false)
	})

	useEffect(() => {
		shouldKeepAwakeRef.current = active && isIonViewActive

		if (!shouldKeepAwakeRef.current) {
			release()
			return
		}

		void request()

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				void request()
			} else {
				release()
			}
		}
		document.addEventListener('visibilitychange', handleVisibilityChange)

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange)
			release()
		}
	}, [ active, isIonViewActive, release, request ])
}
